import classNames from 'classnames'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useOutletContext } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { getLogoForAccount } from '@pages/Trade/TradingAccountInfo/utils/getLogoForAccount'
import { TradingAccountsOutletCtx } from '@pages/Traders-Room/Trading-Accounts/TradingAccounts.page'
import { MobileTradeButtonLinkWrapper } from '@pages/Traders-Room/Trading-Accounts/components/TradeButtonLinkWrapper'
import { TradingAccountActionsDropdown } from '@pages/Traders-Room/Trading-Accounts/components/TradingAccountActionsDropdown'

import { useSessionEntity } from '@context/EntityContext'
import { useModal } from '@context/ModalsContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Body1, Body2, Caption1 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { Badge } from '@ui/components/Badge/Badge'
import { Button, IconButton } from '@ui/components/Button'
import { Link } from '@ui/components/Link'
import { Tooltip } from '@ui/components/Tooltip'
import { ArrowCircleIcon } from '@ui/icons/ArrowCircle.icon'
import { CaretIcon } from '@ui/icons/Caret.icon'
import { CopyIcon } from '@ui/icons/Copy.icon'
import { InfoIcon } from '@ui/icons/Info.icon'
import { WarningIcon } from '@ui/icons/Warning.icon'

import { TickmillCompaniesEnum } from '@utils/companyName.utils'
import { CommonPaths } from '@utils/path-common'
import { addSwapFreePostfix } from '@utils/text'
import {
  TradingAccountRestrictions,
  isRestricted,
  isTradingAccountSuspended,
} from '@utils/trading-account.utils'
import useSensitiveDataFlag from '@utils/useSensitiveDataFlag'
import { WalletRestrictions, isRestricted as isWalletRestricted } from '@utils/wallet.utils'

import { PlatformType, isPlatformTypeDisabled } from '@model/PlatformTypeDto'
import {
  getDisplayPlatformName,
  isMT4PlatformType,
  isMT5PlatformType,
  isTMTPlatformType,
  isTradingViewPlatformType,
  isWebTraderAvailable,
} from '@model/PlatformTypeEnum'
import {
  DemoTradingAccount,
  TradingAccount,
  hasRestrictions,
  isTradingAccountStatusCloseOnly,
  isTradingAccountStatusReadOnly,
} from '@model/TradingAccount'
import { WalletDto } from '@model/WalletDto'

import { useDispatchOpenChat } from '@hooks/useDispatchOpenChat'
import { useFormatNumber } from '@hooks/useFormatNumber'
import { useFundingNav } from '@hooks/useFundingNav'
import { usePlatformTradeUrl } from '@hooks/usePlatformTradeUrl'
import { useScrollToElementIds } from '@hooks/useScrollToElementIds'

import { Box } from '@shared/components/Box'
import { CopyLink } from '@shared/components/CopyLink'
import { InformationModal } from '@shared/modals/Information.modal'

import { SwapFreeContainer } from './components/SwapFreeContainer'

import styles from './MobileTradingAccountCard.module.scss'

interface DetailRowProps {
  label: string
  value: React.ReactNode
  inactive?: boolean
}

const DetailRow: FC<DetailRowProps> = ({ label, value, inactive }) => (
  <Box className={classNames(styles.subtitle, { [styles.inactive]: inactive })}>
    <Body2 className={styles.label}>{label}</Body2>
    <Body2 className={styles.value}>{value}</Body2>
  </Box>
)

interface AccountHeaderProps {
  tradingAccount: TradingAccount
  currencyId: string
  accountName: string
  formattedBalance: string
  hasAllRestrictions: boolean
  isActive: boolean
}

const AccountHeader: FC<AccountHeaderProps> = ({
  tradingAccount,
  accountName,
  formattedBalance,
  hasAllRestrictions,
  isActive,
}) => {
  const { t } = useTranslation()
  return (
    <Box flex gap='x12'>
      <Box className={styles.logoCircle}>{getLogoForAccount(tradingAccount)}</Box>
      <Box textAlign='start'>
        <Box flex gap='x8' align='center' wrap>
          {hasAllRestrictions && <WarningIcon size={20} color={'text-danger-high-em'} />}
          <Caption1 isParagraph>{accountName}</Caption1>
          {!isActive &&
            !isTradingAccountStatusReadOnly(tradingAccount.status) &&
            !isTradingAccountStatusCloseOnly(tradingAccount.status) && (
              <Badge type='yellow' size='xxs'>
                {t('Deactivated')}
              </Badge>
            )}
          {isTradingAccountStatusReadOnly(tradingAccount.status) && (
            <Badge type='yellow' size='xxs' contrast='low'>
              {t('Trading Account.Read Only')}
            </Badge>
          )}
          {isTradingAccountStatusCloseOnly(tradingAccount.status) && (
            <Badge type='yellow' size='xxs' contrast='low'>
              {t('Trading Account.Close Only')}
            </Badge>
          )}
        </Box>
        <Box>
          <Body1 semibold>
            <SensitiveData>{formattedBalance}</SensitiveData>
          </Body1>
        </Box>
      </Box>
    </Box>
  )
}

interface AccountActionsProps {
  tradingAccount: TradingAccount
  hasCampaign: boolean
  isTransferFromRestricted: boolean
  isDepositRestricted: boolean
  hasAllRestrictions: boolean
  getPlatformTradeUrl: (
    account: TradingAccount | DemoTradingAccount | undefined
  ) => Promise<string | undefined>
  onTransfer: () => void
  onDeposit: () => void
  onResetPassword: () => void
  handleChangePasswordRestricted: () => void
  handleResetPasswordRestricted: () => void
  handleChangeInvestorPasswordRestricted: () => void
  handleSetSelectedTradingAccount: (tradingAccount: TradingAccount) => void
  openResetPasswordModal: () => void
}

const AccountActions: FC<AccountActionsProps> = ({
  tradingAccount,
  isTransferFromRestricted,
  isDepositRestricted,
  onTransfer,
  onDeposit,
  handleChangePasswordRestricted,
  handleChangeInvestorPasswordRestricted,
  handleResetPasswordRestricted,
  handleSetSelectedTradingAccount,
  openResetPasswordModal,
}) => {
  const { t } = useTranslation()
  const handleResetPassword = () => {
    handleSetSelectedTradingAccount(tradingAccount)
    openResetPasswordModal()
  }
  return (
    <Box flex gap='x8' mt='x16'>
      <Box fullWidth>
        <MobileTradeButtonLinkWrapper tradingAccount={tradingAccount} />
      </Box>
      <Box fullWidth>
        <Tooltip
          fullWidth
          disabled={!isDepositRestricted}
          content={t('Trading Account.Deposits for this trading account are unavailable')}
        >
          <Button.Secondary size='md' fullWidth disabled={isDepositRestricted} onClick={onDeposit}>
            {t('Deposit')}
          </Button.Secondary>
        </Tooltip>
      </Box>
      <Box>
        <TradingAccountActionsDropdown
          tradingAccount={tradingAccount}
          restrictions={hasRestrictions(tradingAccount) ? tradingAccount.restrictions : []}
          onChangePasswordRestrictedClick={handleChangePasswordRestricted}
          onChangeInvestorPasswordRestrictedClick={handleChangeInvestorPasswordRestricted}
          isTransferFromRestricted={isTransferFromRestricted}
          onTransferClick={onTransfer}
          onResetPasswordClick={handleResetPassword}
          onResetPasswordRestrictedClick={handleResetPasswordRestricted}
        />
      </Box>
    </Box>
  )
}

type LeverageType = {
  tradingAccount: TradingAccount
  restricted?: boolean
}

const expandedStateByCardId = new Map<string, boolean>()

interface TradingAccountCardProps {
  tradingAccount: TradingAccount
  wallet?: WalletDto
  platformTypes?: PlatformType[]
  onLeverageChange?({ tradingAccount, restricted }: LeverageType): void
  onDisplayDynamicLeverageInfo?(): void
  isDeeplink?: boolean
}

export const MobileTradingAccountCard: React.FC<TradingAccountCardProps> = (props) => {
  const { tradingAccount, platformTypes, wallet } = props
  const cardId = tradingAccount.id
  const {
    handleSetSelectedTradingAccount,
    handleChangePasswordRestricted,
    handleResetPasswordRestricted,
    handleChangeInvestorPasswordRestricted,
    openResetPasswordModal,
  } = useOutletContext<TradingAccountsOutletCtx>()

  const { t } = useTranslation()
  const { scrollIntoView } = useScrollToElementIds()
  const { formatMoney } = useFormatNumber()
  const navigate = useNavigate()
  const { navigateToFunding } = useFundingNav()
  const { getPlatformTradeUrl } = usePlatformTradeUrl()

  const [expanded, setExpanded] = useState<boolean>(
    () => expandedStateByCardId.get(cardId) ?? Boolean(props.isDeeplink)
  )

  useEffect(() => {
    expandedStateByCardId.set(cardId, expanded)
  }, [cardId, expanded])

  const computedData = useMemo(() => {
    const hasAllRestrictions = isTradingAccountSuspended(tradingAccount.restrictions)
    const isStatusRestricted = isTradingAccountStatusCloseOnly(tradingAccount.status)
    const isTransferFromRestricted =
      isRestricted(TradingAccountRestrictions.TRANSFER_FROM, tradingAccount.restrictions) ||
      isStatusRestricted
    const hasCampaign = !!tradingAccount.campaigns.length
    const isPlatformDisabled =
      platformTypes && isPlatformTypeDisabled(platformTypes, tradingAccount.platformType.id)
    const isTradeDisabled =
      !isWebTraderAvailable(tradingAccount) &&
      !isTradingAccountStatusCloseOnly(tradingAccount.status)
    const isDepositRestricted =
      isWalletRestricted(WalletRestrictions.DEPOSIT, wallet?.restrictions || []) ||
      isStatusRestricted
    const isActive = !isPlatformDisabled && !isTradeDisabled
    const formattedBalance = formatMoney(
      tradingAccount.platformOverview.balance,
      tradingAccount.currency.id
    )

    return {
      hasAllRestrictions,
      isTransferFromRestricted,
      isDepositRestricted,
      hasCampaign,
      isPlatformDisabled,
      isTradeDisabled,
      isActive,
      formattedBalance,
    }
  }, [tradingAccount, platformTypes, formatMoney])

  const handleTransfer = useCallback(() => {
    const url = computedData.hasCampaign
      ? `/dashboard/traders-room/trading-accounts/${tradingAccount.id}/deposit-to-campaign`
      : `/dashboard/traders-room/trading-accounts/${tradingAccount.id}/transfer`
    navigate(url)
  }, [computedData.hasCampaign, tradingAccount.id, navigate])

  const handleDeposit = useCallback(() => {
    const url = `${CommonPaths.TradersRoomTradingAccounts}/${tradingAccount.id}/deposit`
    navigateToFunding(url)
  }, [tradingAccount.id, navigateToFunding])
  const handleToggleExpand = useCallback(() => {
    if (!expanded && cardId) {
      scrollIntoView([cardId])
    }
    setExpanded((prev) => !prev)
  }, [expanded, cardId, scrollIntoView])

  const handleResetPassword = useCallback(() => {
    handleSetSelectedTradingAccount(tradingAccount)
    openResetPasswordModal()
  }, [tradingAccount, handleSetSelectedTradingAccount, openResetPasswordModal])

  return (
    <Box
      className={classNames(styles.card, { [styles.inactive]: !computedData.isActive })}
      radius='x32'
      gap='x16'
      mt='x16'
    >
      <Box flex justify='space-between'>
        <AccountHeader
          tradingAccount={tradingAccount}
          currencyId={tradingAccount.currency.id}
          accountName={tradingAccount.nickname || tradingAccount.name}
          formattedBalance={computedData.formattedBalance}
          hasAllRestrictions={computedData.hasAllRestrictions}
          isActive={computedData.isActive}
        />
        <IconButton.Ghost
          aria-label={expanded ? t('Collapse details') : t('Expand details')}
          aria-expanded={expanded}
          onClick={handleToggleExpand}
          icon={expanded ? CaretIcon.Up : CaretIcon.Down}
        />
      </Box>
      {expanded && <Expanded {...props} computedData={computedData} />}

      {computedData.isActive && (
        <AccountActions
          tradingAccount={tradingAccount}
          hasCampaign={computedData.hasCampaign}
          isTransferFromRestricted={computedData.isTransferFromRestricted}
          isDepositRestricted={computedData.isDepositRestricted}
          hasAllRestrictions={computedData.hasAllRestrictions}
          getPlatformTradeUrl={getPlatformTradeUrl}
          onTransfer={handleTransfer}
          onResetPassword={handleResetPassword}
          handleChangePasswordRestricted={handleChangePasswordRestricted}
          handleResetPasswordRestricted={handleResetPasswordRestricted}
          handleChangeInvestorPasswordRestricted={handleChangeInvestorPasswordRestricted}
          onDeposit={handleDeposit}
          handleSetSelectedTradingAccount={handleSetSelectedTradingAccount}
          openResetPasswordModal={openResetPasswordModal}
        />
      )}
    </Box>
  )
}

interface ExpandedProps extends TradingAccountCardProps {
  computedData: {
    hasAllRestrictions: boolean
    isTransferFromRestricted: boolean
    hasCampaign: boolean
    isPlatformDisabled: boolean | undefined
    isTradeDisabled: boolean
    isActive: boolean
    formattedBalance: string
  }
}

const Expanded: React.FC<ExpandedProps> = ({
  tradingAccount,
  onDisplayDynamicLeverageInfo,
  onLeverageChange,
  computedData,
}) => {
  const { t } = useTranslation()
  const { formatMoney } = useFormatNumber()
  const navigate = useNavigate()
  const { dispatchOpenChat } = useDispatchOpenChat()

  const isEUEntity = useSessionEntity() === TickmillCompaniesEnum.TICKMILL_EU
  const isMT4 = isMT4PlatformType(tradingAccount.platformType.id)
  const isMT5 = isMT5PlatformType(tradingAccount.platformType.id)
  const equity = formatMoney(tradingAccount.platformOverview.equity, tradingAccount.currency.id)
  const { isActive, hasAllRestrictions, hasCampaign } = computedData
  const TMTInfoModal = useModal(InformationModal, {
    description: t(
      'Trading Account.You can use your Tickmill Trader login credentials to access the TradingView platform'
    ),
    primaryLabel: t('Trading Account.Download'),
    onPrimaryClick: () => {
      TMTInfoModal.close()
      navigate('/dashboard/traders-room/platforms/tickmilltrader/webtrader')
    },
  })
  const TVInfoModal = useModal(InformationModal, {
    description: t(
      'Trading Account.You can use your TradingView login credentials to access the Tickmill Trader Mobile App'
    ),
    primaryLabel: t('Trading Account.Download'),
    onPrimaryClick: () => {
      TVInfoModal.close()
      navigate('/dashboard/traders-room/platforms/tradingview')
    },
  })

  const openPlatformModal = useCallback(() => {
    if (isTMTPlatformType(tradingAccount.platformType.id)) TMTInfoModal.open()
    else if (isTradingViewPlatformType(tradingAccount.platformType.id)) TVInfoModal.open()
  }, [TMTInfoModal, TVInfoModal, tradingAccount.platformType.id])

  return (
    <React.Fragment>
      <Box flex direction='column' gap='x16'>
        <Box className={styles.accountDetails} p='x6' radius='x12' mt='x16'>
          <DetailRow
            label={t('Trading Account.Account Type')}
            value={
              hasCampaign
                ? `${tradingAccount.accountType.name} ${t('Trading Account.Campaign')}`
                : addSwapFreePostfix(tradingAccount.isSwapFree, tradingAccount.accountType.name, t)
            }
            inactive={!isActive}
          />
          <Box
            className={classNames(styles.subtitle, {
              [styles.inactive]: !isActive,
            })}
          >
            <Body2 className={classNames(styles.label, 'is-flex is-align-items-center')}>
              {t('Trading Account.Leverage')}
            </Body2>
            <Box flex align='center' gap='x4'>
              <Link
                state={{ tradingAccountId: tradingAccount.id }}
                onClick={(e) => {
                  if (isActive) {
                    e.stopPropagation()
                    onLeverageChange?.({ tradingAccount, restricted: hasAllRestrictions })
                  }
                }}
              >
                {tradingAccount.leverageType.name}
              </Link>
              {isMT5 && tradingAccount.platformType.dynamicLeverage && (
                <IconButton.Ghost
                  icon={InfoIcon}
                  onClick={(e) => {
                    e.stopPropagation()
                    onDisplayDynamicLeverageInfo?.()
                  }}
                />
              )}
            </Box>
          </Box>
          <DetailRow
            label={t('Trading Account.Equity')}
            value={<SensitiveData>{equity}</SensitiveData>}
            inactive={!isActive}
          />
          {tradingAccount.isSwapFree && tradingAccount.swapFreeLevel && (
            <SwapFreeContainer
              tradingAccount={tradingAccount}
              isFullWidth
              onContactSupport={dispatchOpenChat}
            />
          )}
          {!isEUEntity && tradingAccount.introducingBroker?.name && (
            <DetailRow
              label={t('Trading Account.IB Code')}
              value={tradingAccount.introducingBroker?.name}
              inactive={!isActive}
            />
          )}
          <Box
            className={classNames(styles.subtitle, {
              [styles.inactive]: !isActive,
            })}
          >
            <Body2 className={styles.label}>{t('Trading Account.Platform')}</Body2>
            <Box flex gap='x4' align='center'>
              <Body2 className={styles.value}>
                {getDisplayPlatformName(tradingAccount.platformType)}
              </Body2>
              {isActive &&
                (isTMTPlatformType(tradingAccount.platformType.id) ||
                  isTradingViewPlatformType(tradingAccount.platformType.id)) && (
                  <ActionIcon icon={ArrowCircleIcon.Down} onClick={openPlatformModal} />
                )}
              {isActive && isMT4 && (
                <ActionIcon
                  icon={ArrowCircleIcon.Down}
                  onClick={() => navigate('/dashboard/traders-room/platforms/mt4')}
                />
              )}
              {isActive && isMT5 && (
                <ActionIcon
                  icon={ArrowCircleIcon.Down}
                  onClick={() => navigate('/dashboard/traders-room/platforms/mt5')}
                />
              )}
            </Box>
          </Box>
          {!isEUEntity && tradingAccount.introducingBroker && (
            <DetailRow
              label={t('Trading Account.IB Code')}
              value={tradingAccount.introducingBroker?.name}
              inactive={!isActive}
            />
          )}
        </Box>

        {!computedData.isTradeDisabled && !(isMT4 || isMT5) && (
          <TradingAccountNumber tradingAccount={tradingAccount} />
        )}
        {!computedData.isTradeDisabled && (isMT4 || isMT5) && (
          <TradingAccountServer tradingAccount={tradingAccount} />
        )}
        {(isTMTPlatformType(tradingAccount?.platformType.id) ||
          isTMTPlatformType(tradingAccount?.parentPlatformType?.id)) && (
          <TickmillTraderUsername tradingAccount={tradingAccount} />
        )}
        {!computedData.isTradeDisabled && (isMT4 || isMT5) && (
          <TradingAccountMTLogin tradingAccount={tradingAccount} />
        )}
      </Box>
    </React.Fragment>
  )
}

interface ExpandableTradingAccountProps {
  tradingAccount: TradingAccount | DemoTradingAccount
}

export const TickmillTraderUsername: React.FC<ExpandableTradingAccountProps> = (props) => {
  const { tradingAccount } = props

  const { t } = useTranslation()
  const sensitiveDataFlag = useSensitiveDataFlag()

  return (
    <Box className={styles.subtitle} align='center'>
      <Body2 className={styles.label}>{t('Trading Account.Username')}</Body2>
      <Body2 className={styles.value}>
        <Box className={styles.usernameWrapper} gap='x8' align='center'>
          <Body2>
            <SensitiveData>{tradingAccount.externalId}</SensitiveData>
          </Body2>
          <Body2 className={styles.usernameIcon}>
            <CopyLink value={tradingAccount.externalId} disabled={sensitiveDataFlag}>
              <IconButton.Ghost icon={CopyIcon} size='sm' />
            </CopyLink>
          </Body2>
        </Box>
      </Body2>
    </Box>
  )
}

const TradingAccountMTLogin: React.FC<ExpandableTradingAccountProps> = ({ tradingAccount }) => {
  const { t } = useTranslation()

  if (!tradingAccount?.platformOverview?.login) {
    return null
  }

  return (
    <Box className={styles.subtitle} align='center'>
      <Body2 className={styles.label}>{t('Trading Account.MT Login')}</Body2>
      <Body2 className={styles.value}>
        <Box className={styles.usernameWrapper} gap='x8' align='center'>
          <Body2>{tradingAccount.platformOverview.login}</Body2>
          <Body2 className={styles.usernameIcon}>
            <CopyLink value={tradingAccount.platformOverview.login}>
              <IconButton.Ghost icon={CopyIcon} size='sm' />
            </CopyLink>
          </Body2>
        </Box>
      </Body2>
    </Box>
  )
}

export const TradingAccountNumber: React.FC<ExpandableTradingAccountProps> = ({
  tradingAccount,
}) => {
  const { t } = useTranslation()

  if (!tradingAccount?.name) {
    return null
  }

  return (
    <Box className={styles.subtitle} align='center'>
      <Body2 className={styles.label}>{t('Trading Account.Account Number')}</Body2>
      <Body2 className={styles.value}>
        <Box className={styles.usernameWrapper} gap='x8' align='center'>
          <Body2>{tradingAccount.name}</Body2>
          <Body2 className={styles.usernameIcon}>
            <CopyLink value={tradingAccount.name}>
              <IconButton.Ghost icon={CopyIcon} size='sm' />
            </CopyLink>
          </Body2>
        </Box>
      </Body2>
    </Box>
  )
}

const TradingAccountServer: React.FC<ExpandableTradingAccountProps> = ({ tradingAccount }) => {
  const { t } = useTranslation()

  if (!tradingAccount?.server?.publicName) {
    return null
  }

  return (
    <Box className={styles.subtitle} align='center'>
      <Body2 className={styles.label}>{t('Trading Account.Server')}</Body2>
      <Body2 className={styles.value}>
        <Box className={styles.usernameWrapper} gap='x8' align='center'>
          <Body2>{tradingAccount.server.publicName}</Body2>
          <Body2 className={styles.usernameIcon}>
            <CopyLink value={tradingAccount.server.publicName}>
              <IconButton.Ghost icon={CopyIcon} size='sm' />
            </CopyLink>
          </Body2>
        </Box>
      </Body2>
    </Box>
  )
}
